/**
 * Coverage AutoFill — CAR-4 Zoning fill.
 *
 * Pack: docs/prompt-packs/Coverage_AutoFill_Realworld_Research_Prompt.md (§CAR-4).
 *
 * Pure transform from the Zoning provider chain's aggregated
 * `ZoningData` onto THREE coverage requirements:
 *
 *   - `zoning-confirmation`  ← primaryClass + parcelDistrict (+ falls
 *                              back to NLCD landUseClass label only
 *                              when nothing else populated)
 *   - `special-use-permit`   ← overlays.specialUse[] + municodeRef
 *   - `conservation-overlay` ← overlays.conservation[] + isProtected
 *
 * Mirrors `mapTitleDataToCoverage` shape so AuthorEvidenceDialog
 * prefill behaves uniformly. The single function returns
 * `RequirementCoverage[]` (one entry per requirement that the chain
 * filled), matching the pack-requested signature.
 *
 * Confidence rules (pack §CAR-8 + CAR-4 NLCD floor):
 *   - When NLCD is the ONLY contributing provider, confidence is
 *     hard-clamped to ≤ 0.55 (we emit `low` / 0.45 from the standard
 *     `inferAutoFillConfidence` table since `low < 0.55`). NLCD is a
 *     30 m raster classification — parcel-level certainty would be a
 *     hallucination.
 *   - Otherwise the standard `inferAutoFillConfidence({ fillSource })`
 *     table applies.
 *
 * Hallucination guard:
 *   - Empty ZoningData (no provider returned anything) → returns []
 *     so the resolver downgrades to tier 6.
 *   - NLCD-only chain → only `zoning-confirmation` is emitted (with
 *     the NLCD-clamp confidence + a loud "raster classification, not
 *     parcel zoning" rationale). Special-use / conservation
 *     requirements are NOT emitted because raster cover cannot
 *     attest to ordinance overlays.
 */
import {
  inferAutoFillConfidence,
  type CoverageFillSource,
} from './confidenceTier';
import type { RequirementAutoFill } from './compileCoverageAutoFill';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ZoningData } from '../../services/dataProviders/zoning/types';

/**
 * Per-requirement field-key schemas. Mirrored from
 * `src/config/documentChecklists.ts` (`zoning` checklist item +
 * pack-level requirement extensions). Pinned here so a checklist-side
 * rename can't silently drift the autofiller from the dialog.
 */
export const ZONING_CONFIRMATION_KEYS = [
  'zoningCode',
  'permittedUses',
  'setbacks_ft',
  'heightLimit_ft',
] as const;

export const SPECIAL_USE_KEYS = [
  'specialUseOverlay',
  'ordinanceSection',
] as const;

export const CONSERVATION_OVERLAY_KEYS = [
  'conservationOverlay',
  'isProtected',
] as const;

/** Mirrors `zoning` checklist `authoredSchema` keys (excl. notes). */
export const ZONING_SCHEMA_FIELD_KEYS = [
  'zoningCode',
  'permittedUses',
  'setbacks_ft',
  'heightLimit_ft',
  'specialConditions',
] as const;

const REGRID_URL = 'https://regrid.com/api';

/** Provider sentinel strings (Regrid defaults, etc.) — not attestation. */
export function isProviderPlaceholderString(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  if (normalized.length === 0) return true;
  return (
    normalized === 'unknown'
    || normalized === 'n/a'
    || normalized === 'na'
    || normalized === 'tbd'
    || normalized === '?'
    || normalized === 'none'
    || normalized === 'not available'
    || normalized === 'pending'
  );
}

export function normalizeZoningFieldValue(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (isProviderPlaceholderString(trimmed)) return null;
  return trimmed;
}

function splitPermittedUses(value: unknown): readonly string[] {
  const normalized = normalizeZoningFieldValue(value);
  if (!normalized) return [];
  return normalized
    .split(/[,|]/)
    .map(s => s.trim())
    .filter(s => s.length > 0 && !isProviderPlaceholderString(s));
}

/**
 * Reconstruct `ZoningData` from the live SED parcel slot (Regrid) plus
 * any county-GIS auto-fill fields stamped on `provenanceDetail`.
 */
export function parcelSedToZoningData(sed: SiteEvidenceDocument): ZoningData | null {
  const parcel = sed.parcel;
  if (!parcel || parcel.status === 'unavailable') return null;

  const stored = parcel.provenanceDetail?.autoFillFields ?? {};
  const data = parcel.data;

  const primaryClass =
    normalizeZoningFieldValue(stored.zoningCode)
    ?? normalizeZoningFieldValue(data?.zoning)
    ?? null;
  const landUseLabel =
    normalizeZoningFieldValue(stored.permittedUses)
    ?? normalizeZoningFieldValue(data?.landUse)
    ?? null;

  const setbacksFt =
    typeof stored.setbacks_ft === 'number' && Number.isFinite(stored.setbacks_ft)
      ? stored.setbacks_ft
      : null;
  const heightLimitFt =
    typeof stored.heightLimit_ft === 'number' && Number.isFinite(stored.heightLimit_ft)
      ? stored.heightLimit_ft
      : null;

  const permittedUses = [
    ...splitPermittedUses(stored.permittedUses),
    ...(splitPermittedUses(data?.landUse).filter(u => !splitPermittedUses(stored.permittedUses).includes(u))),
  ];

  if (!primaryClass && !landUseLabel && permittedUses.length === 0 && setbacksFt == null && heightLimitFt == null) {
    return null;
  }

  return {
    primaryClass,
    parcelDistrict: landUseLabel,
    landUseClass: null,
    setbacksFt,
    heightLimitFt,
    permittedUses,
    overlays: [],
    isProtected: null,
    municodeRef: null,
  };
}

function inferZoningContributingProviders(sed: SiteEvidenceDocument): ReadonlyArray<string> {
  const detail = sed.parcel?.provenanceDetail;
  if (detail?.autoFillProviders && detail.autoFillProviders.length > 0) {
    return detail.autoFillProviders;
  }
  if (detail?.autoFillProvider) return [detail.autoFillProvider];
  const source = (sed.parcel?.source ?? '').toLowerCase();
  if (source.includes('regrid')) return ['regrid-parcels'];
  return ['regrid-parcels'];
}

/**
 * Build a CAR auto-fill registry entry for checklist id `zoning` from
 * the SED parcel slot. Pure — no HTTP.
 */
export function zoningSedToRequirementAutoFill(
  sed: SiteEvidenceDocument,
): RequirementAutoFill | null {
  const zoningData = parcelSedToZoningData(sed);
  if (!zoningData) return null;

  const providers = inferZoningContributingProviders(sed);
  const mapped = mapZoningDataToCoverage(zoningData, 'provider', providers);
  const zc = mapped.find(e => e.requirementId === 'zoning-confirmation');
  if (!zc || zc.fieldsFilledCount === 0) return null;

  const fields: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(zc.fields)) {
    if (typeof v === 'string') fields[k] = v;
    else if (typeof v === 'number' && Number.isFinite(v)) fields[k] = v;
  }
  if (Object.keys(fields).length === 0) return null;

  const regridOnly = providers.every(p => p === 'regrid-parcels' || p === 'regrid-title');
  const notes = regridOnly
    ? `${zc.notes} Regrid parcel metadata may be stale — upload county zoning verification before binding commitments.`
    : zc.notes;

  const confidence = regridOnly
    ? {
        tier: 'low' as const,
        value: 0.45 as const,
        rationale: 'Regrid parcel metadata — zoning class only; verify with municipality.',
      }
    : {
        tier: zc.confidence.tier,
        value: (zc.confidence.tier === 'high'
          ? 0.75
          : zc.confidence.tier === 'medium'
            ? 0.6
            : 0.45) as 0.45 | 0.6 | 0.75,
        rationale: zc.confidence.rationale,
      };

  return {
    requirementId: 'zoning',
    fields,
    notes,
    confidence,
    sourceProvider: providers[0] ?? 'regrid-parcels',
    sourceUrl: zc.sourceUrl || REGRID_URL,
    fieldsFilledCount: Object.keys(fields).length,
    fieldsTotalCount: ZONING_SCHEMA_FIELD_KEYS.length,
  };
}

/**
 * One coverage entry the resolver consumes. Mirrors the shape returned
 * by the existing tier-3 path (rationale + confidence + sourceUrl).
 *
 * `requirementId` is the checklist requirement key — one of
 * `'zoning-confirmation' | 'special-use-permit' | 'conservation-overlay'`.
 * Kept as a free string to avoid coupling this module to the
 * `PROVIDER_REQUIREMENT_MAP` literal union.
 */
export interface RequirementCoverage {
  requirementId: string;
  fields: Record<string, string | number | boolean>;
  notes: string;
  fieldsFilledCount: number;
  fieldsTotalCount: number;
  sourceUrl: string;
  sourceProviders: ReadonlyArray<string>;
  confidence: {
    tier: 'low' | 'medium' | 'high';
    value: number;
    rationale: string;
  };
}

const COUNTY_GIS_URL = 'https://library.municode.com/'; // generic fallback when no per-county URL available
const ESRI_URL = 'https://livingatlas.arcgis.com/';
const NLCD_URL = 'https://www.mrlc.gov/data/nlcd-2021-land-cover-conus';

/**
 * NLCD confidence floor — pack hard rule. The standard
 * `inferAutoFillConfidence('provider', 1/1, 1/1)` returns `high` /
 * 0.75 which would be MISLEADING for a raster classification. We
 * substitute a manual `low` / 0.45 (which satisfies the ≤ 0.55 cap)
 * + a custom rationale explaining the clamp.
 */
function nlcdClampedConfidence(
  fieldsFilledCount: number,
  fieldsTotalCount: number,
): RequirementCoverage['confidence'] {
  return {
    tier: 'low',
    value: 0.45,
    rationale:
      `NLCD raster fallback (${fieldsFilledCount}/${fieldsTotalCount} fields, 30 m classification). ` +
      `Cap ≤ 0.55 — raster cover is NOT a parcel-level zoning class. ` +
      `Upload county zoning verification or confirm with municipal code.`,
  };
}

/**
 * Map a `ZoningData` payload to the per-requirement coverage entries.
 *
 * @param zoning              Aggregated ZoningData from the chain.
 * @param fillSource          Override for confidence — passes through
 *                            to `inferAutoFillConfidence`. Default
 *                            `'provider'`.
 * @param contributingProviders  Ordered ids of providers that
 *                            contributed non-null data. The first
 *                            non-empty one drives `sourceUrl`.
 */
export function mapZoningDataToCoverage(
  zoning: ZoningData,
  fillSource: CoverageFillSource = 'provider',
  contributingProviders: ReadonlyArray<string> = [],
  /**
   * CAR-8 follow-up — provider freshness in days. When ≥ 90 the
   * standard (non-NLCD) confidence band is downgraded one tier.
   * `undefined` (default) ⇒ no behaviour change.
   *
   * NOTE: the NLCD-only path is hard-clamped to `low` via
   * `nlcdClampedConfidence`; staleness cannot degrade it further
   * (low → low). So we only forward the freshness signal to the
   * standard `inferAutoFillConfidence` call sites.
   */
  providerFreshnessDays?: number,
): ReadonlyArray<RequirementCoverage> {
  const results: RequirementCoverage[] = [];

  // Sanitize once — NaN / ±Infinity collapse to undefined so the
  // downstream cap check is a clean numeric compare.
  const freshnessDays: number | undefined =
    Number.isFinite(providerFreshnessDays) ? providerFreshnessDays : undefined;

  const nlcdOnly =
    contributingProviders.length > 0 &&
    contributingProviders.every((p) => p === 'nlcd-landuse-zoning');

  // ── zoning-confirmation ──────────────────────────────────────────
  const zcFields: Record<string, string | number | boolean> = {};
  let zcFilled = 0;
  if (zoning.primaryClass) {
    zcFields.zoningCode = zoning.primaryClass;
    zcFilled += 1;
  } else if (zoning.landUseClass && nlcdOnly) {
    // NLCD-only fallback — surface the raster label in the zoningCode
    // slot so the report writer at least sees a value, but the
    // confidence clamp + notes string both call out the rastered
    // nature of the fill.
    zcFields.zoningCode = zoning.landUseClass;
    zcFilled += 1;
  }
  if (zoning.permittedUses.length > 0) {
    zcFields.permittedUses = zoning.permittedUses.join(', ');
    zcFilled += 1;
  }
  if (zoning.setbacksFt != null && Number.isFinite(zoning.setbacksFt)) {
    zcFields.setbacks_ft = zoning.setbacksFt;
    zcFilled += 1;
  }
  if (zoning.heightLimitFt != null && Number.isFinite(zoning.heightLimitFt)) {
    zcFields.heightLimit_ft = zoning.heightLimitFt;
    zcFilled += 1;
  }

  if (zcFilled > 0) {
    const zcConfidence = nlcdOnly
      ? nlcdClampedConfidence(zcFilled, ZONING_CONFIRMATION_KEYS.length)
      : inferAutoFillConfidence({
          fillSource,
          fieldsFilledCount: zcFilled,
          fieldsTotalCount: ZONING_CONFIRMATION_KEYS.length,
          providerFreshnessDays: freshnessDays,
        });
    results.push({
      requirementId: 'zoning-confirmation',
      fields: zcFields,
      notes: buildZoningConfirmationNotes({ zoning, contributingProviders, nlcdOnly }),
      fieldsFilledCount: zcFilled,
      fieldsTotalCount: ZONING_CONFIRMATION_KEYS.length,
      sourceUrl: pickSourceUrl(zoning, contributingProviders, nlcdOnly),
      sourceProviders: contributingProviders,
      confidence: zcConfidence,
    });
  }

  // ── special-use-permit ─────────────────────────────────────────────
  // Only emit when we actually have a special-use overlay reference —
  // NLCD-only chains do NOT contribute here (raster cover can't
  // attest to ordinance overlays).
  if (!nlcdOnly) {
    const specialUseOverlays = zoning.overlays.filter((o) => o.kind === 'special-use');
    if (specialUseOverlays.length > 0 || zoning.municodeRef) {
      const suFields: Record<string, string | number | boolean> = {};
      let suFilled = 0;
      if (specialUseOverlays.length > 0) {
        suFields.specialUseOverlay = specialUseOverlays.map((o) => o.name).join('; ');
        suFilled += 1;
      }
      if (zoning.municodeRef) {
        // ordinanceSection = municodeRef.section when present, else
        // the first overlay's ordinanceSection.
        const section =
          zoning.municodeRef.section ||
          specialUseOverlays.find((o) => o.ordinanceSection !== null)?.ordinanceSection ||
          '';
        if (section) {
          suFields.ordinanceSection = section;
          suFilled += 1;
        }
      } else {
        const overlaySection = specialUseOverlays.find((o) => o.ordinanceSection !== null)?.ordinanceSection;
        if (overlaySection) {
          suFields.ordinanceSection = overlaySection;
          suFilled += 1;
        }
      }
      if (suFilled > 0) {
        results.push({
          requirementId: 'special-use-permit',
          fields: suFields,
          notes: buildSpecialUseNotes({ zoning, specialUseOverlays, contributingProviders }),
          fieldsFilledCount: suFilled,
          fieldsTotalCount: SPECIAL_USE_KEYS.length,
          sourceUrl: zoning.municodeRef?.url ?? COUNTY_GIS_URL,
          sourceProviders: contributingProviders,
          confidence: inferAutoFillConfidence({
            fillSource,
            fieldsFilledCount: suFilled,
            fieldsTotalCount: SPECIAL_USE_KEYS.length,
            providerFreshnessDays: freshnessDays,
          }),
        });
      }
    }
  }

  // ── conservation-overlay ───────────────────────────────────────────
  // Conservation overlay needs either an explicit isProtected flag OR
  // a detected conservation overlay. NLCD-only chains do NOT
  // contribute here either (same rationale).
  if (!nlcdOnly) {
    const conservationOverlays = zoning.overlays.filter((o) => o.kind === 'conservation');
    const hasSignal = conservationOverlays.length > 0 || zoning.isProtected === true;
    if (hasSignal) {
      const coFields: Record<string, string | number | boolean> = {};
      let coFilled = 0;
      if (conservationOverlays.length > 0) {
        coFields.conservationOverlay = conservationOverlays.map((o) => o.name).join('; ');
        coFilled += 1;
      } else {
        // isProtected true with no overlay detail — still surface a
        // banner-style string.
        coFields.conservationOverlay = 'Conservation district detected (no overlay name returned).';
        coFilled += 1;
      }
      if (zoning.isProtected === true) {
        coFields.isProtected = true;
        coFilled += 1;
      }
      results.push({
        requirementId: 'conservation-overlay',
        fields: coFields,
        notes: buildConservationNotes({ zoning, conservationOverlays, contributingProviders }),
        fieldsFilledCount: coFilled,
        fieldsTotalCount: CONSERVATION_OVERLAY_KEYS.length,
        sourceUrl: zoning.municodeRef?.url ?? COUNTY_GIS_URL,
        sourceProviders: contributingProviders,
        confidence: inferAutoFillConfidence({
          fillSource,
          fieldsFilledCount: coFilled,
          fieldsTotalCount: CONSERVATION_OVERLAY_KEYS.length,
          providerFreshnessDays: freshnessDays,
        }),
      });
    }
  }

  return results;
}

function pickSourceUrl(
  zoning: ZoningData,
  contributingProviders: ReadonlyArray<string>,
  nlcdOnly: boolean,
): string {
  if (nlcdOnly) return NLCD_URL;
  if (contributingProviders.includes('esri-living-atlas-zoning')) return ESRI_URL;
  if (zoning.municodeRef) return zoning.municodeRef.url;
  return COUNTY_GIS_URL;
}

function buildZoningConfirmationNotes(args: {
  zoning: ZoningData;
  contributingProviders: ReadonlyArray<string>;
  nlcdOnly: boolean;
}): string {
  const parts: string[] = [];

  if (args.nlcdOnly) {
    parts.push(
      'NLCD / MRLC 30 m RASTER CLASSIFICATION — NOT a parcel-level zoning class. ' +
        'This is a satellite-observed land-cover label (DAM zone-003), surfaced because no ' +
        'parcel-level zoning provider returned data. Verify against county GIS or municipal code ' +
        'BEFORE relying on the zoning slot.',
    );
  } else {
    parts.push(
      'Auto-filled from Zoning provider chain (' +
        args.contributingProviders.join(' + ') +
        ').',
    );
  }

  if (args.zoning.primaryClass) parts.push(`Zoning class: ${args.zoning.primaryClass}.`);
  if (args.zoning.parcelDistrict) parts.push(`District: ${args.zoning.parcelDistrict}.`);
  if (args.zoning.landUseClass && args.nlcdOnly) {
    parts.push(`Raster land cover: ${args.zoning.landUseClass}.`);
  }
  if (args.zoning.setbacksFt != null && Number.isFinite(args.zoning.setbacksFt)) {
    parts.push(`Setback: ${args.zoning.setbacksFt.toFixed(0)} ft.`);
  }
  if (args.zoning.heightLimitFt != null && Number.isFinite(args.zoning.heightLimitFt)) {
    parts.push(`Height limit: ${args.zoning.heightLimitFt.toFixed(0)} ft.`);
  }
  if (args.zoning.permittedUses.length > 0) {
    parts.push(`Permitted uses (${args.zoning.permittedUses.length}): ${args.zoning.permittedUses.slice(0, 5).join(', ')}.`);
  }

  parts.push(
    'Zoning typologies vary by state and county (DAM zone-001). Confirm with the ' +
      'jurisdiction zoning administrator before development commitment.',
  );

  return parts.join(' ');
}

function buildSpecialUseNotes(args: {
  zoning: ZoningData;
  specialUseOverlays: ReadonlyArray<{ name: string; ordinanceSection: string | null }>;
  contributingProviders: ReadonlyArray<string>;
}): string {
  const parts: string[] = [];

  parts.push(
    `Detected ${args.specialUseOverlays.length} special-use overlay reference(s) ` +
      `via ${args.contributingProviders.join(' + ')}.`,
  );

  for (const o of args.specialUseOverlays.slice(0, 3)) {
    parts.push(
      `Overlay "${o.name}"` + (o.ordinanceSection ? ` (${o.ordinanceSection})` : '') + '.',
    );
  }

  if (args.zoning.municodeRef) {
    parts.push(`Ordinance: ${args.zoning.municodeRef.library} ${args.zoning.municodeRef.section}.`);
  }

  parts.push(
    'County process timelines for special-use permits typically run 90-180 days (DAM zone-002). ' +
      'Confirm timeline + cost with the jurisdiction planning office.',
  );

  return parts.join(' ');
}

function buildConservationNotes(args: {
  zoning: ZoningData;
  conservationOverlays: ReadonlyArray<{ name: string; ordinanceSection: string | null }>;
  contributingProviders: ReadonlyArray<string>;
}): string {
  const parts: string[] = [];

  if (args.zoning.isProtected === true) {
    parts.push(
      'CONSERVATION OVERLAY DETECTED. Development may be restricted or prohibited; ' +
        'confirm with the holding entity (USFWS / state DEP / land trust) before commitment.',
    );
  }

  if (args.conservationOverlays.length > 0) {
    parts.push(
      `Detected ${args.conservationOverlays.length} conservation overlay reference(s) ` +
        `via ${args.contributingProviders.join(' + ')}.`,
    );
    for (const o of args.conservationOverlays.slice(0, 3)) {
      parts.push(
        `Overlay "${o.name}"` + (o.ordinanceSection ? ` (${o.ordinanceSection})` : '') + '.',
      );
    }
  }

  if (args.zoning.municodeRef) {
    parts.push(`Ordinance: ${args.zoning.municodeRef.library} ${args.zoning.municodeRef.section}.`);
  }

  parts.push(
    'Conservation-overlay frequency varies by region (DAM zone-003) — FEMA SFHA + ' +
      'USFWS critical-habitat overlap is common in the southeast / mid-Atlantic. ' +
      'Cross-check FEMA NFHL + USFWS IPaC pulls before committing.',
  );

  return parts.join(' ');
}
